import { graphlib } from '@dagrejs/dagre';
import { ENUM__error_type } from '@shared/asserts/ENUM__error_type';
import {
  assert_array_has_1_item,
  assert_equal,
  assert_instanceof,
  assert_not_equal,
  assert_string_object_id,
  assert_type_enum,
  wrong_case,
} from '@shared/asserts/utils_asserts';
import { DTO__base } from '@shared/common/DTO__base';
import { ENUM__string_id_empty } from '@shared/constants/ENUM__string_id_empty';
import { ENUM__ml_asset_family } from '@shared/dto/+model/sd/_enums/ENUM__ml_asset_family';
import { MAP__serializable } from '@shared/dto/_base/MAP__serializable';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { MAP__fields_by_hash, MAP__fields_by_id, MAP__fields_by_role } from '@shared/types/types';
import { FIELD__base } from '../_fields/_base/FIELD__base';
import { FIELD_PRIMITIVE__seed } from '../_fields/_primitives/FIELD_PRIMITIVE__seed';
import { ENUM__image_asset_category } from '../_fields/ENUM__image_asset_category';
import { FIELD__image_asset } from '../_fields/image/FIELD__image_asset';
import { FIELD__image_segments } from '../_fields/image/FIELD__image_segments';
import { FIELD_GRID2D_MODE__lora_vs_lora } from '../_fields/ml_asset/guidance_lora/FIELD_GRID2D_MODE__lora_vs_lora';
import { FIELD_GRID2D_MODE__token_vs_token } from '../_fields/token/FIELD_GRID2D_MODE__token_vs_token';
import { ENUM__invocation_role } from '../_invocations/_base/ENUM__invocation_role';
import { INVOCATION__base } from '../_invocations/_base/INVOCATION__base';
import { INVOCATION_GRID2D__view } from '../_invocations/grid2d/INVOCATION_GRID2D__view';
import { INVOCATION_LATENTS__create_noise } from '../_invocations/latents/INVOCATION_LATENTS__create_noise';
import { ENUM__generation_output_type, ENUM_UTILS__generation_output_type } from '../_templates/_enums/ENUM__generation_output_type';
import { DTO_GRAPH__edge } from './DTO_GRAPH__edge';
import { DTO_GRAPH_META__info } from './DTO_GRAPH_META__info';

// id == generation id
export class DTO_GRAPH__instance extends DTO__base {
  /*
   * STATIC FIELDS
   */

  static _class_name_static = ENUM__dto_class_name.GRAPH;
  /*
   * STATIC METHODS
   */

  static CREATE_NEW() {
    let out = new DTO_GRAPH__instance();
    out.create_meta();
    out.set_graph_id(ENUM__string_id_empty.OBJECT_ID);

    return out;
  }

  /*
   * FIELDS
   */
  nodes_map: MAP__serializable<string, INVOCATION__base>;
  edges: DTO_GRAPH__edge[];

  meta!: DTO_GRAPH_META__info;

  //key - source_id
  fields_ids_map__by_source_id!: MAP__serializable<string, string[]>;

  //key - target_id
  fields_ids_map__by_target_id!: MAP__serializable<string, string>;

  nodes_ids__exceptions_to_process!: string[];

  cache__fields_map_of_sources!: MAP__fields_by_id;

  /*
   * PROPERTIES
   */

  set_graph_id(graph_id: string) {
    if (this.meta.graph_id == graph_id) return;

    assert_string_object_id(graph_id);

    this.meta.graph_id = graph_id;

    for (let node of this.nodes_map.values()) {
      node.meta.graph_id = graph_id;

      let fields = node.get_fields_map(true).values();

      for (let field of fields) field.meta.graph_id = graph_id;
    }
  }

  get invocation_grid2d_view(): INVOCATION_GRID2D__view {
    let out_list = [...this.nodes_map.values()].filter(n => n instanceof INVOCATION_GRID2D__view);

    assert_array_has_1_item(out_list);

    let out = out_list[0];
    assert_instanceof(out, INVOCATION_GRID2D__view);

    return out;
  }

  /*
   * EVENTS
   */

  /*
   * METHODS
   */

  constructor() {
    super();

    this.nodes_map = new MAP__serializable();
    this.edges = [];

    this.meta = DTO_GRAPH_META__info.CREATE_STUB();

    this.fields_ids_map__by_source_id = new MAP__serializable();
    this.fields_ids_map__by_target_id = new MAP__serializable();

    this.nodes_ids__exceptions_to_process = [];

    this.cache__fields_map_of_sources = new MAP__serializable();
  }

  get_graph_family() {
    let family_sets = new Set<ENUM__ml_asset_family>();

    for (let node of this.nodes__get_all()) family_sets.add(node.family);

    family_sets.delete(ENUM__ml_asset_family.NONE);

    let out;

    if (family_sets.size == 0) {
      out = ENUM__ml_asset_family.NONE;
    } else if (family_sets.size == 1) {
      out = family_sets.values().next().value;
      assert_type_enum(out, ENUM__ml_asset_family);
    } else {
      out = ENUM__ml_asset_family.MIXED;
    }

    return out;
  }

  hash_create() {
    wrong_case();
    return '';
  }

  //calls revalidate inside
  rehash() {
    this.hash_clear();

    this.revalidate();

    this.hash_update_for_nodes();
  }

  hash_update_for_nodes() {
    let graph_dagre = this.dagre__convert_to();

    let is_acyclic = graphlib.alg.isAcyclic(graph_dagre);

    let nodes_ids__exceptions_to_process_set = new Set(this.nodes_ids__exceptions_to_process);

    /*
     *
     */
    //key - field_id

    /*
     *
     */

    for (let node of this.nodes_map.values()) {
      node.fields_map_cache__init();

      let fields_map = node.get_fields_map(true);

      this.fields__add_to_map_of_sources(fields_map);
    }

    /*
     *
     */

    for (let node of this.nodes_map.values()) {
      if (nodes_ids__exceptions_to_process_set.has(node.meta.node_id)) continue;

      if (!is_acyclic) {
        node.hash_hard__set_to_empty(this);
        continue;
      }

      let nodes_ids_to_check = this.dagre__get_nodes_ids_to(graph_dagre, node.meta.node_id);
      nodes_ids_to_check.push(node.meta.node_id);

      let nodes = nodes_ids_to_check.map(id => this.node__get(id)); //todo: remove

      let node_or_incomers_contains_error = nodes.some(node => !node.meta.errors.is_empty());

      node_or_incomers_contains_error ? node.hash_hard__set_to_empty(this) : node.hash__update_with_graph(this);
    }

    /*
     *
     */

    this.fields_cache__clear();
  }

  /*
   *
   */

  fields_cache__init() {
    this.cache__fields_map_of_sources = new MAP__serializable<string, FIELD__base>();

    for (let node of this.nodes_map.values()) {
      node.fields_map_cache__init();
    }

    for (let node of this.nodes_map.values()) {
      let fields_map = node.get_fields_map(true);

      this.fields__add_to_map_of_sources(fields_map);
    }
  }

  fields__add_to_map_of_sources(fields_map: MAP__fields_by_id) {
    for (const [field_id, field] of fields_map.entries()) {
      if (this.cache__fields_map_of_sources.has(field_id)) continue;

      let field_source = field.find_source(this);

      this.cache__fields_map_of_sources.set(field_id, field_source);
    }
  }

  fields_cache__clear() {
    this.cache__fields_map_of_sources = new MAP__serializable();

    for (let node of this.nodes_map.values()) {
      node.fields_map_cache__clear();
    }
  }

  /*
   *
   */

  hash_clear() {
    for (const node of this.nodes_map.values()) {
      if (this.nodes_ids__exceptions_to_process.includes(node.meta.node_id)) continue;

      node.hash__clear(this);
    }
  }

  /*
   *
   */
  create_meta() {
    this.meta = new DTO_GRAPH_META__info();
  }

  remove_disconnected_nodes_and_edges() {
    let unused_nodes = this.nodes_map.values().filter(node => !node.meta.is_connected_to_grid2d);

    for (let node of unused_nodes) this.node__delete(node.meta.node_id);
  }

  get_fields_to_generate() {
    //key - field hash
    let out: MAP__fields_by_hash = new MAP__serializable();

    let fields_images = this.fields__get_by_type(FIELD__image_asset);
    fields_images = fields_images.map(field => field.find_source(this)); //get sources
    fields_images = fields_images.filter(field => !field.get_has_content()); //keep only without content

    let fields_segments = this.fields__get_by_type(FIELD__image_segments);
    fields_segments = fields_segments.filter(field => !field.hash_sha256_content_source.is_empty_hash_sha256()); //keep only with content to segment

    let fields = [...fields_images, ...fields_segments];

    fields = fields.filter(field => !field.is_stub()); //skip stubs
    fields = fields.filter(field => field.meta.kind__is_output); //skip computed

    for (let field of fields) out.set(field.meta.field_hash__value_full, field);

    return out;
  }

  stubify_images() {
    let fields = this.fields__get_by_type(FIELD__image_asset);
    fields = fields.filter(field => !field.meta.data__is_linked); //keep only sources

    for (let field of fields) field.image__asset_stubify();
  }

  /*
   * NODE LAYOUT
   */

  node_layout__overlaps() {
    let out = false;

    let nodes = Array.from(this.nodes__get_all().values());

    for (let i = 0; i < nodes.length; i++) {
      if (out) break;

      const node_a = nodes[i];

      for (let j = i + 1; j < nodes.length; j++) {
        const node_b = nodes[j];

        if (!node_b.meta.is_intersects_with(node_a.meta)) continue;

        out = true;

        break;
      }
    }

    return out;
  }

  field_get__noise_seed() {
    let out = FIELD_PRIMITIVE__seed.CREATE_STUB();

    //todo: need check out_node is affects to generation
    let out_node = this.nodes_map.values().find(node => node instanceof INVOCATION_LATENTS__create_noise);

    if (!out_node) return out;

    return out_node.seed;
  }

  field_image__get_under_mask_for(field: FIELD__image_asset) {
    assert_equal(field.category, ENUM__image_asset_category.MASK);

    let out = FIELD__image_asset.CREATE_STUB();

    if (!this.nodes_map.has(field.meta.node_id)) return out;

    let node = this.nodes_map.get_or_assert(field.meta.node_id);

    out = node.field_image__get_under_mask_for(field);

    return out;
  }

  field_image__get_mask_for(field: FIELD__image_asset) {
    let out = FIELD__image_asset.CREATE_STUB();

    if (!this.nodes_map.has(field.meta.node_id)) return out;

    let node = this.nodes_map.get_or_assert(field.meta.node_id);

    out = node.field_image__get_mask_for(field);

    out = out.find_source(this);

    return out;
  }

  ui__disable() {
    let fields_map = this.fields__get_map();

    for (let field of fields_map.values()) {
      field.meta.ui__is_visible = false;
    }
  }

  is_equal(other: DTO_GRAPH__instance) {
    let out = true;

    if (this.nodes_map.size() != other.nodes_map.size()) return false;

    /*
     * NODES STRUCTURE
     */

    for (let node of this.nodes_map.values()) {
      if (!out) break;

      if (other.nodes_map.has(node.meta.node_id)) continue;

      out = false;
    }

    if (!out) return false;

    /*
     * FIELDS STRUCTURE
     */

    let fields_map_this = this.fields__get_map();
    let fields_map_other = other.fields__get_map();

    if (fields_map_this.size() != fields_map_other.size()) out = false;

    for (let field of fields_map_this.values()) {
      if (!out) break;

      if (fields_map_other.has(field.meta.field_id)) continue;

      out = false;
    }

    if (!out) return false;

    /*
     * FIELDS CONTENT
     */

    for (let field_this of fields_map_this.values()) {
      if (!out) break;

      let field_other = fields_map_other.get_or_assert(field_this.meta.field_id);

      if (field_this.is_equal(field_other)) continue;

      out = false;
    }

    return out;
  }

  output_type__update() {
    let type = ENUM__generation_output_type.UNKNOWN;

    if (!this.node_role__has_node(ENUM__invocation_role.GRID2D__VIEW)) return this.output_type__set(type);

    let grid2d_view = this.node_role__get_node(ENUM__invocation_role.GRID2D__VIEW);
    assert_instanceof(grid2d_view, INVOCATION_GRID2D__view);

    type = ENUM_UTILS__generation_output_type.get_type_for_image(grid2d_view.image_out.category);

    this.output_type__set(type);
  }

  output_type__set(type: ENUM__generation_output_type) {
    this.meta.output_type = type;
  }

  node__has_effect_on_generation_runtime(node: INVOCATION__base) {
    if (this.is_stub()) return false;
    if (this.meta.graph_id != node.meta.graph_id) return false;
    if (!this.nodes_map.has(node.meta.node_id)) return false;

    let node_from_graph = this.nodes_map.get_or_assert(node.meta.node_id);
    let out = node_from_graph.has_effect_on_generation_runtime(this);

    return out;
  }

  node_and_field__has_effect_on_generation_runtime(field: FIELD__base) {
    let { meta } = field;

    if (this.is_stub()) return false;
    if (this.meta.graph_id != meta.graph_id) return false;
    if (!this.nodes_map.has(meta.node_id)) return false;

    let node = this.node__get(meta.node_id);
    if (!node.has_effect_on_generation_runtime(this)) return false;

    let field_source = field.find_source(this);
    if (!field_source.has_effect_on_generation_runtime(this)) return false;

    return true;
  }

  /*
   *
   */

  fields__update_aliases() {
    let fields_by_role: MAP__fields_by_role = new MAP__serializable();

    for (let node of this.nodes_map.values()) {
      let fields_map = node.get_fields_map(true);

      fields_by_role.merge_with(FIELD__base.map_by_field_role(fields_map.values()));
    }

    /*
     *
     */

    for (let node of this.nodes_map.values()) {
      node.update_fields_aliases(fields_by_role);

      let fields_map = node.get_fields_map(true);

      for (let field of fields_map.values()) {
        if (field.is_stub()) continue; //skip stubs

        field.update_fields_aliases(fields_by_role);
      }
    }
  }

  fields__prepare() {
    for (let node of this.nodes_map.values()) {
      if (this.nodes_ids__exceptions_to_process.includes(node.meta.node_id)) continue;

      let fields_map = node.get_fields_map(true);

      for (let field of fields_map.values()) {
        if (field.is_stub()) continue; //skip stubs
        if (field.meta.data__is_linked) continue; //skip linked fields
        if (field.meta.kind__is_output) continue; //skip computed fields

        field.prepare_for_usage_in(this);
      }
    }
  }

  nodes__prepare() {
    for (let node of this.nodes_map.values()) {
      if (this.nodes_ids__exceptions_to_process.includes(node.meta.node_id)) continue;

      node.prepare_for_usage_in(this);
    }
  }

  /*
   * ASSERTS
   */

  assert_standard_fields(): void {}

  assert_grid2d_dimensions() {
    let [grid2d_row_count, grid2d_column_count] = this.grid2d__get_fields_dimensions();

    assert_equal(grid2d_row_count.length, 1, { assert_type: ENUM__error_type.GRAPH__MISSING_GRID2D_ROW });
    assert_equal(grid2d_column_count.length, 1, { assert_type: ENUM__error_type.GRAPH__MISSING_GRID2D_COLUMN });

    let [grid2d_row, grid2d_column] = [this.grid2d__get_field_row(), this.grid2d__get_field_column()];

    //only for token vs token we allowed same column and row
    if (grid2d_row instanceof FIELD_GRID2D_MODE__token_vs_token) {
      //do nothing
    } else if (grid2d_row instanceof FIELD_GRID2D_MODE__lora_vs_lora) {
      //do nothing
    } else {
      assert_not_equal(grid2d_row.meta.field_id, grid2d_column.meta.field_id);
    }
  }
}
